import React, { useEffect } from 'react';
import { gql, useQuery } from '@common/hooks';
import Iframe from 'react-iframe';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import {
  AppContent,
  ErrorHandler,
  LoadingState,
  Tabs,
} from '@common/components';

export const AnalyticsReportsQuery = gql`
  query AnalyticsReports {
    worksReportDashboards {
      name
      membership
      embedUrl
      externalKey
      guestToken
      host
      nativeFilters
    }
  }
`;

const styleElement = (
  <style jsx>
    {`
      .reports-wrapper {
        height: 100%;
        width: 100%;
        position: relative;
      }
      .reports-wrapper:after {
        // Covers retool logo
        content: '';
        z-index: 1;
        width: 100%;
        height: 30px;
        background-color: #f8f8f9;
        position: absolute;
        bottom: 0;
      }
      .reports-wrapper iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        border: 0;
      }
    `}
  </style>
);

const LookerContentReport = ({ embedUrl }: { embedUrl: string }) => {
  return (
    <>
      <section
        id="looker-reports-wrapper"
        className="reports-wrapper"
        data-testid="looker-reports-wrapper"
      >
        <Iframe url={embedUrl} />
      </section>
      {styleElement}
    </>
  );
};

type SupersetProps = {
  membership: string;
  externalKey: string;
  host: string;
  guestToken: string;
  nativeFilters: string;
};

const SupersetContentReport = ({
  externalKey,
  host,
  guestToken,
  nativeFilters,
  membership,
}: SupersetProps) => {
  const reportElement = `superset-reports-wrapper-${externalKey}`;

  useEffect(() => {
    const report = document.getElementById(reportElement);

    const embed = async () => {
      await embedDashboard({
        id: externalKey,
        supersetDomain: host,
        mountPoint: report as HTMLElement,
        fetchGuestToken: async () => guestToken,
        dashboardUiConfig: {
          hideTitle: false,
          hideTab: false,
          hideChartControls: false,
          filters: {
            expanded: true,
            visible: true,
          },
          urlParams: {
            native_filters: nativeFilters,
            membership: membership,
          },
        },
        debug: false,
        referrerPolicy: 'strict-origin-when-cross-origin',
      });
    };

    if (report) {
      embed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section
        id={reportElement}
        className="reports-wrapper"
        data-testid={reportElement}
      />
      {styleElement}
    </>
  );
};

type worksReportDashboardsProps = {
  membership: string;
  name: string;
  embedUrl: string;
  externalKey: string;
  host: string;
  guestToken: string;
  nativeFilters: string;
};

export function ReportsPage() {
  const { loading, error, data } = useQuery(AnalyticsReportsQuery);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="ReportsPage" />;
  }

  setBreadcrumbs([{ name: 'Reports', path: '/works/reports' }]);

  return (
    <AppContent>
      <Tabs>
        {data.worksReportDashboards.map((item: worksReportDashboardsProps) => (
          <Tabs.Pane
            key={item.name}
            style={{ height: 'calc(100vh - 160px)' }}
            tab={item.name}
          >
            {item.embedUrl ? (
              <LookerContentReport embedUrl={item.embedUrl} />
            ) : (
              <SupersetContentReport
                externalKey={item.externalKey}
                host={item.host}
                guestToken={item.guestToken}
                nativeFilters={item.nativeFilters}
                membership={item.membership}
              />
            )}
          </Tabs.Pane>
        ))}
      </Tabs>
    </AppContent>
  );
}
